<template>
  <v-container>
    <v-row class="flex-row">
      <v-col cols="12" md="7">
        <div class="d-flex justify-space-between align-center mb-4">
          <div>
            <h2 class="mb-1">Super Admin Monitor</h2>
            <div class="text-body-2 text-medium-emphasis" v-if="terminData">
              {{ terminData.webi_title }} · Beginn: {{ terminData.beginn }}
            </div>
          </div>
          <v-chip :color="isConnected ? 'green' : 'orange'" variant="flat">
            {{ isConnected ? 'verbunden' : 'verbinde...' }}
          </v-chip>
        </div>

        <v-alert
          v-if="!isConnected"
          type="info"
          variant="tonal"
          class="mb-4"
        >
          Wir verbinden Sie automatisch mit dem Webinar-Raum …
        </v-alert>

        <div
          v-if="audioPlaybackBlocked"
          class="audio-unlock-banner mb-4"
        >
          <v-icon
            size="28"
            color="red-darken-2"
            class="mr-3"
          >
            mdi-volume-off
          </v-icon>
          <div class="flex-grow-1">
            <div class="font-weight-medium">
              Audio konnte nicht automatisch gestartet werden.
            </div>
            <div class="text-caption text-medium-emphasis">
              Bitte klicken Sie auf „Ton aktivieren", damit der Teilnehmer-Ton abgespielt wird.
            </div>
          </div>
          <v-btn
            color="primary"
            variant="tonal"
            class="ml-4"
            @click="unlockAudioPlayback"
          >
            Ton aktivieren
          </v-btn>
        </div>

        <v-card class="mb-4" variant="outlined">
          <v-card-title class="d-flex align-center justify-space-between">
            <span>Dozenten-Video & Präsentation</span>
            <v-chip v-if="presentationStarted" size="small" color="primary" variant="tonal">
              Präsentation läuft{{ slideLabel ? ` · Folie ${slideLabel}` : '' }}
            </v-chip>
          </v-card-title>
          <v-card-text>
            <div
              class="superadmin-video-stage"
              :class="{ 'superadmin-video-stage--presentation': presentationStarted }"
            >
              <template v-if="presentationStarted">
                <div class="superadmin-presentation-stage">
                  <!-- <div class="superadmin-slide-info">
                    Slide: {{ activePictureBroadCast.url || '—' }} ({{ activePictureBroadCast.type || 'unbekannt' }}) · src: {{ presentationSrc || '—' }} · webi: {{ slideShowId || '—' }}
                  </div> -->
                  <div v-if="!presentationSrc" class="superadmin-slide-placeholder">
                    Warte auf Präsentations-Slide …
                  </div>
                  <img
                    v-else-if="activePictureBroadCast.type === 'picture'"
                    class="superadmin-presentation-media"
                    :src="presentationSrc"
                  >
                  <video
                    v-else
                    class="superadmin-presentation-media"
                    :src="presentationSrc"
                    controls
                    preload="auto"
                  >
                    Ihr Browser unterstützt das Video-Element nicht.
                  </video>
                </div>
                <!-- Dozenten-Video neben den Folien statt als Overlay (verdeckte sonst Folieninhalt) -->
                <div class="superadmin-dozent-side">
                  <div
                    v-show="hasAdminVideo"
                    id="admin-videos-inline"
                    class="superadmin-dozent-side__stage"
                  ></div>
                  <div
                    v-if="!hasAdminVideo"
                    class="superadmin-dozent-side__stage superadmin-dozent-side__placeholder"
                  >
                    Kein Dozenten-Video
                  </div>
                  <div class="superadmin-dozent-side__label">
                    <span v-if="hasAdminVideo" class="superadmin-dozent-side__live">LIVE</span>
                    Dozent
                  </div>
                </div>
              </template>
              <div
                v-show="!presentationStarted"
                id="admin-videos-fullsize"
                class="superadmin-admin-fullsize"
              ></div>
            </div>
            <div class="text-right mt-1">
              <a href="#" class="superadmin-debug-toggle" @click.prevent="showDebug = !showDebug">
                {{ showDebug ? 'Technik-Infos ausblenden' : 'Technik-Infos' }}
              </a>
            </div>
            <div v-if="showDebug" class="superadmin-presentation-debug">
              Presentation: {{ presentationStarted ? 'an' : 'aus' }}
              · slide: {{ activePictureBroadCast.url || '—' }}
              · src: {{ presentationSrc || '—' }}
              · webi: {{ slideShowId || '—' }}
              · last: {{ lastDataTrackSummary }}
            </div>
          </v-card-text>
        </v-card>

     
      </v-col>

      <v-col cols="12" md="5">
        <template v-if="isConnected">
          <h3 class="mb-3" v-if="activeBroadcastParticipants.length > 0">Live Teilnahmen</h3>
          <v-row dense>
          <v-col
            cols="12"
            md="6"
              v-for="participant in activeBroadcastParticipants"
            :key="participant.identity"
          >
            <div class="video-card">
              <div class="video-card-header">
                <div>
                  <strong>{{ getNameFromTeilnehmer(participant.identity) }}</strong>
                  <div class="text-caption">{{ participant.identity }}</div>
                </div>
                <v-chip
                  size="small"
                  :color="participant.isBroadcasting ? 'green' : 'grey'"
                  variant="flat"
                >
                  {{ participant.isBroadcasting ? 'Live' : 'Offline' }}
                </v-chip>
              </div>
              <div
                class="video-area"
                :id="`video-container-${participant.identity.replace('_','-')}`"
              ></div>
              <div class="video-card-meta">
                <v-icon :color="participant.hasVideo ? 'green' : 'red'">
                  {{ participant.hasVideo ? 'mdi-video' : 'mdi-video-off' }}
                </v-icon>
                <v-icon :color="participant.hasAudio ? 'green' : 'red'">
                  {{ participant.hasAudio ? 'mdi-microphone' : 'mdi-microphone-off' }}
                </v-icon>
              </div>
            </div>
          </v-col>
          </v-row>
        </template>
        <v-card class="mb-4" variant="outlined">
          <v-card-title>Meine Übertragung</v-card-title>
          <v-card-text>
            <div class="d-flex flex-wrap gap-2 mb-3">
              <v-btn
              class="mr-2"
                size="small"
                :color="isVideoPublished || isAudioPublished ? 'green' : 'red'"
                variant="elevated"
                @click="toggleLocalBroadcast"
              >
                <v-icon start>
                  {{ (isVideoPublished || isAudioPublished) ? 'mdi-video-off' : 'mdi-video' }}
                </v-icon>
                {{ (isVideoPublished || isAudioPublished) ? 'Video & Audio an' : 'Video & Audio aus' }}
              </v-btn>
              <v-btn
                class="mr-2"
                size="small"
                :color="isVideoPublished ? 'green' : 'red'"
                variant="elevated"
                @click="toggleLocalVideoOnly"
              >
                <v-icon start>{{ isVideoPublished ? 'mdi-video-off' : 'mdi-video' }}</v-icon>
                {{ isVideoPublished ? 'Video an' : 'Video aus' }}
              </v-btn>
              <v-btn
                size="small"
                :color="isAudioPublished ? 'green' : 'red'"
                variant="elevated"
                @click="toggleLocalAudio"
              >
                <v-icon start>{{ isAudioPublished ? 'mdi-microphone-off' : 'mdi-microphone' }}</v-icon>
                {{ isAudioPublished ? 'Mikro an' : 'Mikro aus' }}
              </v-btn>
            </div>
            <div
              v-if="isMyBroadcasting"
              id="superadmin-local-video"
              class="video-area superadmin-local-video"
            ></div>
            <div v-else class="text-body-2 text-medium-emphasis">
              Sie sind aktuell nicht auf Sendung.
            </div>
          </v-card-text>
        </v-card>
        <v-card class="mb-4" variant="outlined">
          <v-card-title class="d-flex align-center justify-space-between">
            <span>Teilnehmer-Chat</span>
            <v-btn
              size="small"
              color="red"
              variant="elevated"
              @click="deleteAllChats"
            >
              Chats löschen
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div ref="chatMessagesContainer" class="chat-list">
              <div
                v-for="(msg, idx) in chatMessages"
                :key="idx"
                class="chat-message"
                :class="{ 'chat-message--me': msg.from === myIdentity }"
              >
                <div class="text-body-2 mb-1">{{ msg.text }}</div>
                <div class="text-right">
                  <span class="text-caption text-grey-darken-1">
                    <b>{{ msg.from === myIdentity ? 'Ich' : (msg.displayName || getNameFromTeilnehmer(msg.from)) }}</b>
                    · {{ formatTime(msg.ts) }} · {{ getTimeAgo(msg.ts) }}
                  </span>
                </div>
              </div>
              <div v-if="chatMessages.length === 0" class="text-center text-medium-emphasis">
                Noch keine Nachrichten im Chat
              </div>
            </div>
            <v-textarea
              v-model="chatInput"
              label="Schreiben Sie Ihre Nachricht hier und senden Sie sie mit Enter"
              rows="2"
              hide-details
              auto-grow
              class="mt-2"
              @keydown.enter.prevent="sendChatMessage"
            />
          </v-card-text>
        </v-card>

        <v-card variant="outlined">
          <v-card-title>Teilnehmer steuern</v-card-title>
          <v-card-text>
            <div
              v-for="item in getParticipantsBroadcastStatus()"
              :key="item.identity"
              class="participant-control"
            >
              <div>
                <strong>{{ getNameFromTeilnehmer(item.identity) }}</strong>
                <div class="text-caption">{{ item.identity }}</div>
              </div>
              <div class="d-flex gap-2">
                <v-btn
                  size="x-small"
                  color="green"
                  @click="setUserVideoOnDirect(1, item)"
                  v-if="AUDIENCE_LIVE_ENABLED && !item.isBroadcasting"
                >
                  Live einschalten
                </v-btn>
                <v-btn
                  size="x-small"
                  color="red"
                  @click="setUserVideoOnDirect(0, item)"
                  v-if="item.isBroadcasting"
                >
                  Live stoppen
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { inject, ref, onMounted, onBeforeUnmount, computed, watch, nextTick, shallowRef, markRaw } from 'vue';
import { useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { AUDIENCE_LIVE_ENABLED } from '@/services/webinarFeatures';

const Video = require('twilio-video');

const baseCalls = inject('baseCalls');
const loaderStore = inject('loaderStore');
const updateDocumentHead = inject('updateDocumentHead');

const route = useRoute();
const userStore = useUserStore();

const room = shallowRef(null);
const isConnected = ref(false);
const isConnecting = ref(false);
const token = ref('');
const terminData = ref(null);
const superAdminIdentity = ref('');
const localDataTrack = shallowRef(null);
const dataTrackMessages = ref([]);
const lastDataTrackPayload = ref(null);
const chatInput = ref('');
const chatMessages = ref([]);
const initialChatLoaded = ref(false);
const allTeilnehmerInRoom = ref([]);
const audience = ref([]);
const localVideoTrack = shallowRef(null);
const localAudioTrack = shallowRef(null);
const isMyBroadcasting = ref(false);
const isVideoPublished = ref(false);
const isAudioPublished = ref(false);
const participantBroadcastStatusMap = ref(new Map());
const audienceIdentityHasVideo = ref({});
const audienceIdentityHasAudio = ref({});
const audioPlaybackBlocked = ref(false);
const pendingAudioElements = new Set();
let audioUnlockHandler = null;
const chatMessagesContainer = ref(null);
const hasAdminVideo = ref(false);
const showDebug = ref(false);
const presentationStarted = ref(false);
const slideShowNumber = ref(0);
const activePictureBroadCast = ref({ url: '', type: 'picture' });
const sentChatMessageKeys = new Set();
const processedChatMessageKeys = new Set();

const slideShowId = computed(() => {
  return (
    slideShowNumber.value ||
    terminData.value?.wter_webinar_id ||
    terminData.value?.webi_id ||
    0
  );
});

// "15.jpg" -> "15" für die Anzeige im Kopf der Präsentation
const slideLabel = computed(() => {
  const url = activePictureBroadCast.value?.url || '';
  return url.split('/').pop().replace(/\.[^.]+$/, '');
});

const presentationSrc = computed(() => {
  const url = activePictureBroadCast.value?.url;
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('/')) return url;
  if (!slideShowId.value) return url;
  return `/videos/webinar${slideShowId.value}/slides/${url}`;
});

const myIdentity = computed(() => superAdminIdentity.value || `superadmin_${route.params.wteil_id}`);

async function initialize() {
  await getSuperAdminToken();
  await getAudience();
  await loadInitialChatHistory();
  if (terminData.value) {
    updateDocumentHead?.(
      `Super Admin · ${terminData.value.webi_title}`,
      terminData.value.webi_title
    );
  }
}

async function getSuperAdminToken() {
  loaderStore?.setLoader(true, 'Super Admin verbindet …');
  const data = {
    action: 'getSuperAdminToken',
    wter_id: route.params.wter_id,
    wteil_id: route.params.wteil_id,
  };
  const response = await baseCalls.webinarPost(data);
  loaderStore?.setLoader(false);
  if (response.data.success) {
    token.value = response.data.token;
    terminData.value = response.data.terminData;
    if (terminData.value?.wter_webinar_id) {
      slideShowNumber.value = terminData.value.wter_webinar_id;
    } else if (terminData.value?.webi_id) {
      slideShowNumber.value = terminData.value.webi_id;
    }
    superAdminIdentity.value = response.data.identity;
    userStore.setToken(response.data.token);
    userStore.setWebinarTerminId(response.data.terminId);
    await joinRoom();
    await loadInitialChatHistory();
  } else {
    console.error('SuperAdmin Token Fehler', response.data.message);
  }
}

async function loadInitialChatHistory() {
  try {
    if (!terminData.value || !terminData.value.wter_id || initialChatLoaded.value) return;

    const data = {
      action: 'getFragen',
      id: terminData.value.wter_id,
    };
    console.log('✅ Lade initiale Chat-History (SuperAdmin) - alle Chats des Termins:', data);
    const response = await baseCalls.webinarPost(data);
    
    if (response.data.success === true && Array.isArray(response.data.fragen) && response.data.fragen.length > 0) {
      const mapped = response.data.fragen.map((item) => ({
        from: 'audience_' + item.teilnehmerid,
        displayName: item.name,
        text: item.frage,
        ts: Date.now() - (item.vor || 0) * 60 * 1000,
      }));
      // Daten kommen bereits chronologisch sortiert aus DB (älteste zuerst)
      chatMessages.value = mapped;
      initialChatLoaded.value = true;
      console.log('✅ Initiale Chat-History (SuperAdmin) geladen:', mapped.length, 'Nachrichten');
      // Nach initialem Laden zum neuesten Chat scrollen (mit mehreren Versuchen)
      scrollChatToBottomForced();
    }
  } catch (error) {
    console.error('❌ Fehler beim Laden der Chat-History für SuperAdmin:', error);
  }
}

async function joinRoom() {
  if (isConnected.value || isConnecting.value || !token.value) return;
  isConnecting.value = true;

  const dataTrack = new Video.LocalDataTrack({
    name: 'super-admin-chat',
    ordered: true,
    reliable: true
  });
  localDataTrack.value = markRaw(dataTrack);

  try {
    const r = await Video.connect(token.value, {
      name: `webinar_${terminData.value.id}`,
      tracks: [localDataTrack.value],
      video: false,
      audio: false,
      automaticSubscription: true
    });
    room.value = markRaw(r);
    isConnected.value = true;
    isConnecting.value = false;

    room.value.on('participantConnected', setupParticipant);
    room.value.on('participantDisconnected', participant => {
      teardownParticipant(participant);
      updateAllTeilnehmerInRoom();
    });
    room.value.on('disconnected', () => {
      isConnected.value = false;
      room.value = null;
    });

    const participants = Array.from(room.value.participants.values());
    participants.forEach(setupParticipant);
    updateAllTeilnehmerInRoom();
  } catch (error) {
    console.error('❌ SuperAdmin connect error', error);
    isConnected.value = false;
    isConnecting.value = false;
  }
}

function setupParticipant(participant) {
  console.log('👤 Setup participant:', participant.identity);
  updateAllTeilnehmerInRoom();
  evaluateParticipantBroadcastStatus(participant);

  // Proaktiv vorhandene DataTracks binden (falls bereits subscribed)
  try {
    const dataPubs = Array.from(participant.dataTracks?.values?.() || []);
    dataPubs.forEach(pub => {
      if (pub && pub.track) {
        attachDataTrack(pub.track, participant);
      }
    });
  } catch (e) {
    console.warn('DataTrack-Bind (setupParticipant) fehlgeschlagen:', e);
  }

  participant.tracks.forEach(publication => {
    console.log(
      '🔎 Publication:',
      publication.kind,
      'sid:',
      publication.trackSid,
      'isSubscribed:',
      publication.isSubscribed
    );
    // Admin-Video priorisieren und ggf. proaktiv abonnieren
    if (publication.kind === 'video') {
      try {
        if (participant.identity.startsWith('admin_') && typeof publication.setPriority === 'function') {
          publication.setPriority('high');
        }
      } catch (e) {
        console.warn('⚠️ setPriority fehlgeschlagen:', e);
      }
    }
    if (publication.kind === 'video' && participant.identity.startsWith('admin_')) {
      try {
        if (!publication.isSubscribed && typeof publication.subscribe === 'function') {
          publication.subscribe();
        }
      } catch (e) {
        console.warn('⚠️ subscribe() auf Publication fehlgeschlagen:', e);
      }
    }
    // DataTracks proaktiv subscriben (für Präsentations-Events)
    if (publication.kind === 'data' && !publication.isSubscribed && typeof publication.subscribe === 'function') {
      try {
        publication.subscribe();
      } catch (e) {
        console.warn('DataTrack subscribe fehlgeschlagen:', e);
      }
    }
    if (publication.isSubscribed && publication.track) {
      attachTrack(publication.track, participant);
    }
    publication.on('subscribed', track => {
      attachTrack(track, participant);
      evaluateParticipantBroadcastStatus(participant);
      updateAllTeilnehmerInRoom();
    });
    publication.on('unsubscribed', track => {
      detachTrack(track);
      evaluateParticipantBroadcastStatus(participant);
      updateAllTeilnehmerInRoom();
    });
  });

  participant.on('trackSubscribed', track => {
    console.log('✅ trackSubscribed:', track.kind, participant.identity);
    attachTrack(track, participant);
    evaluateParticipantBroadcastStatus(participant);
    updateAllTeilnehmerInRoom();
  });

  participant.on('trackUnsubscribed', track => {
    detachTrack(track);
    evaluateParticipantBroadcastStatus(participant);
    updateAllTeilnehmerInRoom();
  });

  participant.on('trackPublished', publication => {
    console.log('📡 trackPublished:', publication.kind, participant.identity, publication.trackSid);
    if (publication.kind === 'video' && participant.identity.startsWith('admin_')) {
      try {
        if (typeof publication.setPriority === 'function') {
          publication.setPriority('high');
        }
        if (!publication.isSubscribed && typeof publication.subscribe === 'function') {
          publication.subscribe();
        }
      } catch (e) {
        console.warn('⚠️ subscribe() auf Publication fehlgeschlagen:', e);
      }
    }
    if (publication.kind === 'data' && !publication.isSubscribed && typeof publication.subscribe === 'function') {
      try {
        publication.subscribe();
      } catch (e) {
        console.warn('DataTrack subscribe (trackPublished) fehlgeschlagen:', e);
      }
    }
  });
}

function teardownParticipant(participant) {
  participant.tracks.forEach(publication => {
    if (publication.track) {
      detachTrack(publication.track);
    }
  });
}

function attachTrack(track, participant) {
  if (track.kind === 'video') {
    attachVideoTrack(track, participant);
  } else if (track.kind === 'audio') {
    attachAudioTrack(track, participant);
  } else if (track.kind === 'data') {
    attachDataTrack(track, participant);
  }
}

async function attachVideoTrack(track, participant) {
  console.log('🎥 Attach video:', participant.identity, 'sid:', track.sid);
  await nextTick();
  let containerId;
  if (participant.identity.startsWith('admin_')) {
    containerId = presentationStarted.value ? 'admin-videos-inline' : 'admin-videos-fullsize';
  } else {
    containerId = `video-container-${participant.identity.replace('_', '-')}`;
  }
  let container = document.getElementById(containerId);
  if (!container) {
    console.warn('Video container not found yet, retrying …', containerId);
    setTimeout(() => attachVideoTrack(track, participant), 300);
    return;
  }
  const target = container.querySelector('.video-area') || container;
  const el = track.attach();
  el.setAttribute('data-track-id', track.sid);
  el.setAttribute('data-participant-identity', participant.identity);
  el.autoplay = true;
  el.playsInline = true;
  el.muted = true;
  el.style.width = '100%';
  el.style.height = '100%';
  el.style.objectFit = 'cover';
  target.innerHTML = '';
  target.appendChild(el);
  if (participant.identity.startsWith('admin_')) {
    hasAdminVideo.value = true;
  } else {
    audienceIdentityHasVideo.value[participant.identity] = true;
  }
}

async function ensureAdminVideoVisible() {
  await nextTick();
  const targetId = presentationStarted.value ? 'admin-videos-inline' : 'admin-videos-fullsize';
  const target = document.getElementById(targetId);
  if (!target) return;

  // Bereits vorhandenes Admin-Video in den korrekten Container verschieben
  const existingAdminVideos = Array.from(
    document.querySelectorAll('video[data-participant-identity^="admin_"]')
  );
  if (existingAdminVideos.length > 0) {
    target.innerHTML = '';
    existingAdminVideos.forEach(video => {
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';
      target.appendChild(video);
    });
    hasAdminVideo.value = true;
    return;
  }

  // Falls kein DOM-Video vorhanden: aus dem aktuell subscribten Track neu attachen
  if (!room.value) return;
  const adminParticipant = Array.from(room.value.participants.values()).find(p =>
    p.identity.startsWith('admin_')
  );
  if (!adminParticipant) return;
  const adminVideoPub = Array.from(adminParticipant.videoTracks?.values?.() || []).find(pub => !!pub.track);
  if (adminVideoPub?.track) {
    await attachVideoTrack(adminVideoPub.track, adminParticipant);
  }
}

function scheduleAudioUnlockListener() {
  if (audioUnlockHandler) return;
  audioUnlockHandler = async () => {
    await resumePendingAudioPlayback();
  };
  document.addEventListener('click', audioUnlockHandler);
}

function teardownAudioUnlockListener() {
  if (!audioUnlockHandler) return;
  document.removeEventListener('click', audioUnlockHandler);
  audioUnlockHandler = null;
}

async function attemptAudioPlayback(audioEl) {
  if (!audioEl) return;
  pendingAudioElements.add(audioEl);
  try {
    await audioEl.play();
    pendingAudioElements.delete(audioEl);
    if (pendingAudioElements.size === 0) {
      audioPlaybackBlocked.value = false;
      teardownAudioUnlockListener();
    }
  } catch (error) {
    if (error?.name !== 'NotAllowedError') {
      console.warn('Audio autoplay fehlgeschlagen:', error);
    }
    audioPlaybackBlocked.value = true;
    scheduleAudioUnlockListener();
  }
}

async function resumePendingAudioPlayback() {
  if (pendingAudioElements.size === 0) {
    audioPlaybackBlocked.value = false;
    teardownAudioUnlockListener();
    return;
  }

  const attempts = Array.from(pendingAudioElements);
  for (const audioEl of attempts) {
    try {
      await audioEl.play();
      pendingAudioElements.delete(audioEl);
    } catch (error) {
      console.warn('Audio Unlock fehlgeschlagen:', error);
    }
  }

  audioPlaybackBlocked.value = pendingAudioElements.size > 0;
  if (audioPlaybackBlocked.value) {
    scheduleAudioUnlockListener();
  } else {
    teardownAudioUnlockListener();
  }
}

async function unlockAudioPlayback() {
  await resumePendingAudioPlayback();
}

function attachAudioTrack(track, participant) {
  const existing = document.querySelector(`[data-track-id="${track.sid}"]`);
  if (existing) return;
  
  const audioEl = track.attach();
  audioEl.setAttribute('data-track-id', track.sid);
  audioEl.setAttribute('data-participant-identity', participant.identity);
  audioEl.autoplay = true;
  audioEl.playsInline = true;
  audioEl.muted = false;
  audioEl.volume = 1;
  audioEl.controls = false;
  audioEl.style.display = 'none';
  document.body.appendChild(audioEl);
  attemptAudioPlayback(audioEl);
  audienceIdentityHasAudio.value[participant.identity] = true;
  console.log('✅ Audio attached für SuperAdmin:', participant.identity);
}

function detachTrack(track) {
  const elements = document.querySelectorAll(`[data-track-id="${track.sid}"]`);
  elements.forEach(el => {
    const identity = el.getAttribute('data-participant-identity');
    if (identity) {
      if (track.kind === 'video') {
        if (identity.startsWith('admin_')) {
          hasAdminVideo.value = false;
        } else {
          audienceIdentityHasVideo.value[identity] = false;
        }
      } else if (track.kind === 'audio') {
        audienceIdentityHasAudio.value[identity] = false;
        // Audio Element aus pendingAudioElements entfernen
        pendingAudioElements.delete(el);
        if (pendingAudioElements.size === 0) {
          audioPlaybackBlocked.value = false;
          teardownAudioUnlockListener();
        }
      }
    }
    el.remove();
  });
}

function updateAllTeilnehmerInRoom() {
  if (!room.value) {
    allTeilnehmerInRoom.value = [];
    return;
  }
  const participants = Array.from(room.value.participants.values()).map(p => {
    const status = participantBroadcastStatusMap.value.get(p.identity) || {};
    return {
      identity: p.identity,
      isBroadcasting: status.isBroadcasting || false,
      hasVideo: status.hasVideo || false,
      hasAudio: status.hasAudio || false
    };
  });
  if (room.value.localParticipant) {
    participants.unshift({
      identity: myIdentity.value,
      isBroadcasting: isMyBroadcasting.value,
      hasVideo: !!localVideoTrack.value,
      hasAudio: !!localAudioTrack.value
    });
  }
  allTeilnehmerInRoom.value = participants;
}

function evaluateParticipantBroadcastStatus(participant) {
  try {
    const hasVideo = Array.from(participant.videoTracks?.values?.() || []).some(pub => !!pub.track);
    const hasAudio = Array.from(participant.audioTracks?.values?.() || []).some(pub => !!pub.track);
    participantBroadcastStatusMap.value.set(participant.identity, {
      hasVideo,
      hasAudio,
      isBroadcasting: hasVideo || hasAudio
    });
  } catch (error) {
    console.warn('evaluateParticipantBroadcastStatus failed', error);
  }
}

function getParticipantsBroadcastStatus() {
  return Array.from(participantBroadcastStatusMap.value.entries()).map(([identity, status]) => ({
    identity,
    ...status
  }));
}

const activeBroadcastParticipants = computed(() =>
  allTeilnehmerInRoom.value.filter(
    p =>
      p.identity !== myIdentity.value &&
      !p.identity.startsWith('admin_') &&
      p.isBroadcasting
  )
);

const lastDataTrackSummary = computed(() => {
  if (!lastDataTrackPayload.value) {
    return 'noch keine Nachrichten';
  }
  const payload = lastDataTrackPayload.value;
  const type = payload.type || 'unbekannt';
  const slide = payload.picture?.url ? ` · Slide: ${payload.picture.url}` : '';
  return `${type}${slide}`;
});


function attachDataTrack(track, participant) {
  if (track._eventListenersBound) return;
  track.on('message', msg => {
    try {
      const text = typeof msg === 'string' ? msg : new TextDecoder().decode(msg);
      let payload;
      try {
        payload = JSON.parse(text);
      } catch (_) {
        payload = { type: 'text', text };
      }
      console.log('📩 DataTrack message (SuperAdmin):', participant.identity, payload);
      lastDataTrackPayload.value = payload;
      if (payload.type === 'pictureBroadCastStarted') {
        presentationStarted.value = true;
      } else if (payload.type === 'pictureBroadCastEnded') {
        presentationStarted.value = false;
      } else if (payload.type === 'pictureBroadCast' && payload.picture) {
        presentationStarted.value = true;
        activePictureBroadCast.value = payload.picture;
      }
      dataTrackMessages.value.push({
        from: participant.identity,
        payload,
        ts: Date.now()
      });
      if (payload && payload.type === 'chatMessage') {
        addChatMessageFromPayload(payload);
      }
    } catch (e) {
      console.warn('DataTrack parse error', e);
    }
  });
  track._eventListenersBound = true;
}

function getMyDataTrack() {
  return localDataTrack.value || null;
}

function sendDataMessage(message) {
  const track = getMyDataTrack();
  if (!track) return false;
  const payload = typeof message === 'string' ? message : JSON.stringify(message);
  track.send(payload);
  return true;
}

function addChatMessageFromPayload(payload) {
  if (!payload || payload.type !== 'chatMessage') return;
  const key = `${payload.from}|${payload.text || ''}|${payload.timestamp || payload.ts || ''}`;
  if (processedChatMessageKeys.has(key)) return;
  
  // Eigene Nachrichten nicht doppelt hinzufügen (wurden bereits in sendChatMessage hinzugefügt)
  if (payload.from === myIdentity.value) {
    if (sentChatMessageKeys.has(key)) {
      sentChatMessageKeys.delete(key);
      return;
    }
  }
  
  // Neueste Nachricht unten anzeigen
  chatMessages.value.push({
    from: payload.from,
    displayName: getNameFromTeilnehmer(payload.from, 'name'),
    text: payload.text,
    ts: payload.timestamp || Date.now()
  });
  processedChatMessageKeys.add(key);
  
  // Zum neuesten Chat scrollen
  scrollChatToBottom();
}

async function saveChatMessageToDb(text) {
  try {
    const data = {
      action: 'saveFrage',
      frage: text,
      terminid: terminData.value.wter_id,
      teilnehmerid: route.params.wteil_id,
    };
    await baseCalls.webinarPost(data);
  } catch (error) {
    console.error('Fehler beim Speichern der Chat-Nachricht:', error);
  }
}

function sendChatMessage() {
  const text = chatInput.value.trim();
  if (!text) return;
  const message = {
    type: 'chatMessage',
    from: myIdentity.value,
    text,
    timestamp: Date.now()
  };
  const key = `${message.from}|${message.text}|${message.timestamp}`;
  sentChatMessageKeys.add(key);
  sendDataMessage(message);
  saveChatMessageToDb(text);
  
  // Eigene Nachricht direkt zum Chat hinzufügen
  chatMessages.value.push({
    from: myIdentity.value,
    displayName: 'Ich',
    text: text,
    ts: Date.now()
  });
  
  chatInput.value = '';
  
  // Zum neuesten Chat scrollen
  scrollChatToBottom();
}

async function deleteAllChats() {
  try {
    const terminId = terminData.value?.wter_id || route.params.wter_id;
    if (!terminId) return;
    const data = {
      action: 'deleteFragenByTermin',
      terminid: terminId,
    };
    const response = await baseCalls.webinarPost(data);
    if (response?.data?.success) {
      chatMessages.value = [];
      processedChatMessageKeys.clear();
      sentChatMessageKeys.clear();
      scrollChatToBottom();
    }
  } catch (error) {
    console.error('Chats löschen fehlgeschlagen:', error);
  }
}

function updateLocalBroadcastFlags() {
  try {
    const lp = room.value?.localParticipant;
    const hasVideo = Array.from(lp?.videoTracks?.values?.() || []).some(pub => !!pub.track);
    const hasAudio = Array.from(lp?.audioTracks?.values?.() || []).some(pub => !!pub.track);
    isVideoPublished.value = hasVideo;
    isAudioPublished.value = hasAudio;
    isMyBroadcasting.value = hasVideo || hasAudio;
  } catch (e) {
    console.warn('updateLocalBroadcastFlags failed', e);
  }
}

async function ensureLocalVideoTrack() {
  if (localVideoTrack.value) return;
  const videoTrack = await Video.createLocalVideoTrack({
    name: `video_${myIdentity.value}`
  });
  localVideoTrack.value = markRaw(videoTrack);
}

async function ensureLocalAudioTrack() {
  if (localAudioTrack.value) return;
  const audioTrack = await Video.createLocalAudioTrack({
    name: `audio_${myIdentity.value}`
  });
  localAudioTrack.value = markRaw(audioTrack);
}

function attachLocalVideoPreview(track, attempt = 0) {
  const container = document.getElementById('superadmin-local-video');
  if (!track) return;
  if (!container) {
    if (attempt < 5) {
      setTimeout(() => attachLocalVideoPreview(track, attempt + 1), 200);
    }
    return;
  }
  const el = track.attach();
  el.muted = true;
  el.style.width = '100%';
  el.style.height = '100%';
  el.style.objectFit = 'contain';
  container.innerHTML = '';
  container.appendChild(el);
}

async function stopLocalVideo() {
  if (room.value && localVideoTrack.value) {
    try {
      await room.value.localParticipant.unpublishTrack(localVideoTrack.value);
    } catch (e) {
      console.warn('unpublishTrack (video) fehlgeschlagen:', e);
    }
    try {
      localVideoTrack.value.stop();
    } catch (e) {
      console.warn('stop() (video) fehlgeschlagen:', e);
    }
    localVideoTrack.value = null;
  }
  const container = document.getElementById('superadmin-local-video');
  if (container) container.innerHTML = '';
}

async function stopLocalAudio() {
  if (room.value && localAudioTrack.value) {
    try {
      await room.value.localParticipant.unpublishTrack(localAudioTrack.value);
    } catch (e) {
      console.warn('unpublishTrack (audio) fehlgeschlagen:', e);
    }
    try {
      localAudioTrack.value.stop();
    } catch (e) {
      console.warn('stop() (audio) fehlgeschlagen:', e);
    }
    localAudioTrack.value = null;
  }
}

async function toggleLocalBroadcast() {
  try {
    if (!room.value) return;
    if (isVideoPublished.value || isAudioPublished.value) {
      await stopLocalVideo();
      await stopLocalAudio();
    } else {
      await ensureLocalVideoTrack();
      await ensureLocalAudioTrack();
      if (localVideoTrack.value) {
        await room.value.localParticipant.publishTrack(localVideoTrack.value);
      }
      if (localAudioTrack.value) {
        await room.value.localParticipant.publishTrack(localAudioTrack.value);
      }
      updateLocalBroadcastFlags();
      await nextTick();
      if (localVideoTrack.value) {
        attachLocalVideoPreview(localVideoTrack.value);
      }
    }
    updateLocalBroadcastFlags();
    updateAllTeilnehmerInRoom();
  } catch (error) {
    console.error('toggleLocalBroadcast error', error);
  }
}

async function toggleLocalVideoOnly() {
  try {
    if (!room.value) return;
    if (isVideoPublished.value) {
      await stopLocalVideo();
    } else {
      await ensureLocalVideoTrack();
      if (localVideoTrack.value) {
        await room.value.localParticipant.publishTrack(localVideoTrack.value);
        updateLocalBroadcastFlags();
        await nextTick();
        attachLocalVideoPreview(localVideoTrack.value);
      }
    }
    updateLocalBroadcastFlags();
    updateAllTeilnehmerInRoom();
  } catch (error) {
    console.error('toggleLocalVideoOnly error', error);
  }
}

async function toggleLocalAudio() {
  try {
    if (!room.value) return;
    if (isAudioPublished.value) {
      await stopLocalAudio();
    } else {
      await ensureLocalAudioTrack();
      if (localAudioTrack.value) {
        await room.value.localParticipant.publishTrack(localAudioTrack.value);
      }
    }
    updateLocalBroadcastFlags();
    updateAllTeilnehmerInRoom();
  } catch (error) {
    console.error('toggleLocalAudio error', error);
  }
}

function getNameFromTeilnehmer(identity, field = 'name') {
  const parts = identity.split('_');
  const id = parts[parts.length - 1];
  const entry = audience.value.find(item => item.wteil_id == id);
  if (!entry) return identity;
  if (field === 'admin') {
    return entry.wteil_is_admin;
  }
  return entry.wteil_pers_name || identity;
}

function getTimeAgo(timestamp) {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  
  if (minutes < 1) return 'gerade eben';
  if (minutes === 1) return 'vor 1 Minute';
  if (minutes < 60) return `vor ${minutes} Minuten`;
  
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return 'vor 1 Stunde';
  if (hours < 24) return `vor ${hours} Stunden`;
  
  const days = Math.floor(hours / 24);
  if (days === 1) return 'vor 1 Tag';
  return `vor ${days} Tagen`;
}

function formatTime(timestamp) {
  const date = new Date(timestamp);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

// Chat nach unten scrollen
function scrollChatToBottom() {
  nextTick(() => {
    if (chatMessagesContainer.value) {
      chatMessagesContainer.value.scrollTop = chatMessagesContainer.value.scrollHeight;
      console.log('📜 Chat gescrollt:', chatMessagesContainer.value.scrollTop, '/', chatMessagesContainer.value.scrollHeight);
    } else {
      console.warn('⚠️ chatMessagesContainer nicht verfügbar für Scroll');
    }
  });
}

// Chat nach unten scrollen mit mehreren Versuchen (für initiales Laden)
function scrollChatToBottomForced() {
  const tryScroll = (attempt = 1) => {
    nextTick(() => {
      if (chatMessagesContainer.value) {
        chatMessagesContainer.value.scrollTop = chatMessagesContainer.value.scrollHeight;
        console.log(`📜 Chat gescrollt (Versuch ${attempt}):`, chatMessagesContainer.value.scrollTop, '/', chatMessagesContainer.value.scrollHeight);
      } else if (attempt < 5) {
        console.log(`⏳ Container noch nicht bereit, Versuch ${attempt}/5...`);
        setTimeout(() => tryScroll(attempt + 1), 200);
      }
    });
  };
  tryScroll();
}

async function getAudience() {
  try {
    const data = {
      action: 'getAudienceAll',
      id: route.params.wter_id
    };
    const response = await baseCalls.webinarPost(data);
    if (response.data.success) {
      audience.value = response.data.audience || [];
    }
  } catch (error) {
    console.error('Audience laden fehlgeschlagen', error);
  }
}

function setUserVideoOnDirect(video, item) {
  const identity = myIdentity.value;
  const toSend = item.identity;
  if (video === 0) {
    sendDataMessage({ type: 'videoOff', to: toSend, from: identity, ts: Date.now() });
  } else {
    sendDataMessage({ type: 'videoOn', to: toSend, from: identity, ts: Date.now() });
  }
}

watch(dataTrackMessages, (list) => {
  if (!list || list.length === 0) return;
  const newest = list[list.length - 1]; // Neueste Nachricht ist jetzt am Ende (wegen push())
  const payload = newest.payload;
  if (!payload) return;
  lastDataTrackPayload.value = payload;

  if (payload.type === 'pictureBroadCastStarted') {
    presentationStarted.value = true;
  }
  if (payload.type === 'pictureBroadCastEnded') {
    presentationStarted.value = false;
  }
  if (payload.type === 'pictureBroadCast' && payload.picture) {
    presentationStarted.value = true;
    activePictureBroadCast.value = payload.picture;
  }

  if (payload.type === 'chatMessage') {
    addChatMessageFromPayload(payload);
  }
});

watch(presentationStarted, async (newValue, oldValue) => {
  if (newValue === oldValue) return;
  await ensureAdminVideoVisible();
  await nextTick();
  const fromId = oldValue ? 'admin-videos-inline' : 'admin-videos-fullsize';
  const toId = newValue ? 'admin-videos-inline' : 'admin-videos-fullsize';
  const from = document.getElementById(fromId);
  const to = document.getElementById(toId);
  if (!from || !to) return;
  const videos = Array.from(from.querySelectorAll('video'));
  videos.forEach(video => {
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'cover';
    to.appendChild(video);
  });
});

// Watcher für Chat-Nachrichten: automatisch nach unten scrollen
watch(chatMessages, () => {
  scrollChatToBottom();
}, { deep: true });

watch(room, (newRoom) => {
  if (newRoom) {
    updateAllTeilnehmerInRoom();
    ensureAdminVideoVisible();
    
    // Wenn verbunden UND Chat bereits geladen ist, zum neuesten Chat scrollen
    if (chatMessages.value.length > 0) {
      scrollChatToBottomForced();
    }
  }
});

onMounted(() => {
  initialize();
});

onBeforeUnmount(() => {
  try {
    if (room.value) {
      room.value.disconnect();
    }
  } catch (error) {
    console.warn('disconnect failed', error);
  }
  
  // Audio cleanup
  teardownAudioUnlockListener();
  pendingAudioElements.forEach((el) => {
    try {
      el.pause();
    } catch (_) {
      /* ignore */
    }
  });
  pendingAudioElements.clear();
  audioPlaybackBlocked.value = false;
});
</script>

<style scoped>
.video-card {
  border: 1px solid #ddd;
  padding: 12px;
  margin-bottom: 12px;
}
.video-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.video-area {
  width: 100%;
  height: 200px;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.superadmin-local-video {
  height: 240px;
  background: transparent;
}
.video-card-meta {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}
.chat-list {
  max-height: 250px;
  min-height: 120px;
  overflow-y: auto;
  margin-bottom: 12px;
}
.superadmin-video-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  overflow: hidden;
}
.superadmin-presentation-stage {
  position: relative;
  width: 100%;
  height: 100%;
}
.superadmin-presentation-media {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.superadmin-slide-info {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 9;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
}
.superadmin-slide-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #111;
  font-size: 14px;
}
.superadmin-presentation-debug {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
  word-break: break-all;
}
/* Präsentation: Folie links, Dozenten-Video als eigene Spalte rechts */
.superadmin-video-stage--presentation {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(180px, 1fr);
  gap: 12px;
  align-items: start;
  aspect-ratio: auto;
  background: transparent;
  overflow: visible;
}
.superadmin-video-stage--presentation .superadmin-presentation-stage {
  aspect-ratio: 16 / 9;
  height: auto;
  background: #fff;
  border: 1px solid #e0e0e0;
  overflow: hidden;
}
.superadmin-dozent-side__stage {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #000;
  overflow: hidden;
}
.superadmin-dozent-side__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  font-size: 13px;
}
.superadmin-dozent-side__label {
  margin-top: 6px;
  font-size: 13px;
  color: #555;
  display: flex;
  align-items: center;
  gap: 6px;
}
.superadmin-dozent-side__live {
  background: #e53935;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  letter-spacing: 0.5px;
}
.superadmin-debug-toggle {
  font-size: 12px;
  color: #999;
  text-decoration: none;
}
@media (max-width: 1100px) {
  .superadmin-video-stage--presentation {
    grid-template-columns: 1fr;
  }
  .superadmin-dozent-side {
    max-width: 320px;
  }
}
.superadmin-admin-fullsize {
  width: 100%;
  height: 100%;
  background: #000;
}
.chat-message {
  border-bottom: 1px solid #eee;
  padding: 6px;
}
.chat-message--me {
  background-color: #e8f5e9; /* helles Grün */
}
.participant-control {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}
.audio-unlock-banner {
  display: flex;
  align-items: center;
  border: 2px solid #ffa726;
  background: #fff7ef;
  padding: 12px 16px;
  border-radius: 0;
  gap: 12px;
}
</style>

